import { Reveal } from "@/components/Reveal";
import { EcgLine } from "@/components/EcgLine";
import { TwelveLeadEcg } from "@/components/TwelveLeadEcg";

const Spotlight = ({ code, title, intro, paragraphs, video, image, media, flip = false, landscape = false, testId }) => (
  <section id={testId.replace("-spotlight", "")} className="bg-navy py-24 lg:py-32 border-t border-ivory/10 scroll-mt-24" data-testid={testId}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
      <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">{code}</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ivory leading-tight mb-4">
            {title}
          </h2>
          <p className="font-serif italic text-lg lg:text-xl text-champagne/90 mb-8">{intro}</p>
        </Reveal>
        {paragraphs.map((p, i) => (
          <Reveal key={i} delay={0.1 + i * 0.08}>
            <p className="text-sm lg:text-base text-ivory/60 leading-relaxed mb-5">{p}</p>
          </Reveal>
        ))}
      </div>

      <Reveal className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`} delay={0.2}>
        {media ? (
          <div className="relative max-w-md mx-auto w-full">
            <div className="absolute -top-4 -right-4 w-full h-full border border-gold/50" />
            {media}
          </div>
        ) : video ? (
          <div className={`relative ${landscape ? "max-w-xl" : "max-w-md"} mx-auto w-full`}>
            <div className="absolute -top-4 -right-4 w-full h-full border border-gold/50" />
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className={`relative w-full ${landscape ? "aspect-[2/1]" : "h-[340px] lg:h-[560px]"} object-cover`}
              data-testid={`${testId}-video`}
            >
              <source src={video.mp4} type="video/mp4" />
              <source src={video.webm} type="video/webm" />
            </video>
          </div>
        ) : image ? (
          <div className="relative max-w-lg mx-auto">
            <div className="absolute -top-4 -right-4 w-full h-full border border-gold/50" />
            <img
              src={image.src}
              alt={image.alt}
              className="relative w-full h-[480px] object-cover"
              data-testid={`${testId}-image`}
            />
          </div>
        ) : (
          <div className="relative max-w-md mx-auto border border-ivory/10 p-10 lg:p-14">
            <div className="absolute -top-4 -right-4 w-full h-full border border-gold/50" />
            <EcgLine className="text-gold h-20 w-full" />
            <div className="mt-10 grid grid-cols-2 gap-px bg-ivory/10 border border-ivory/10">
              {[
                ["Wear Time", "24h – 28 days"],
                ["Leads", "7-Lead Patch"],
                ["Form", "Cable-Free"],
                ["Reporting", "Full Disclosure"],
              ].map(([k, v]) => (
                <div key={k} className="bg-navy p-5">
                  <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-ivory/40">{k}</p>
                  <p className="font-mono text-sm text-champagne mt-1.5">{v}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </Reveal>
    </div>
  </section>
);

export const ServiceSpotlights = () => (
  <>
    <Spotlight
      code="S.01 · Echocardiogram"
      title="Echocardiogram"
      intro="Comprehensive cardiac ultrasound assessment with specialist reporting."
      testId="echo-spotlight"
      video={{ mp4: "/echo_loop.mp4", webm: "/echo_loop.webm" }}
      paragraphs={[
        "CVI provides high-quality transthoracic echocardiography using advanced cardiac ultrasound equipment to assess cardiac structure, function and haemodynamics.",
        "Each examination is accompanied by a comprehensive echocardiography report aligned with British Society of Echocardiography (BSE) standards, providing clear, clinically relevant findings to support patient diagnosis and ongoing management.",
        "Secure cloud-based image storage keeps echocardiographic images and reports available for clinical review. Where further specialist input is required, authorised clinicians — including remote cardiologists — can securely access the study, supporting specialist referral, multidisciplinary review and continuity of care without the need to repeat imaging.",
        "Our echocardiography service can be delivered directly within your practice or healthcare facility, with CVI providing the specialist expertise, cardiac ultrasound equipment and digital imaging infrastructure required.",
      ]}
    />
    <Spotlight
      code="S.02 · 12-Lead ECG"
      title="12-Lead ECG"
      intro="High-quality resting electrocardiography delivered within your clinical setting."
      testId="ecg-spotlight"
      flip
      landscape
      video={{ mp4: "/ecg_machine_loop.mp4", webm: "/ecg_machine_loop.webm" }}
      paragraphs={[
        "CVI provides 12-lead resting ECG acquisition to support cardiovascular assessment, clinical pathways and research protocols.",
        "ECGs can be performed alongside echocardiography or as a standalone diagnostic service, providing a convenient and flexible solution for private healthcare, primary care and clinical research.",
        "Where required, ECG recordings can be incorporated into the wider cardiac diagnostic pathway to support specialist review and onward referral.",
      ]}
    />
    <Spotlight
      code="S.03 · Holter Monitoring"
      title="Holter Monitoring"
      intro="Discreet patch-based ECG monitoring — from 24 hours up to 28 days."
      testId="holter-spotlight"
      media={<TwelveLeadEcg />}
      paragraphs={[
        "A compact, cable-free 7-lead ECG patch is worn comfortably under clothing, providing continuous, high-quality rhythm monitoring throughout daily life — no bulky recorder, no wires, no disruption.",
        "Extended wear dramatically improves the detection of intermittent arrhythmias, palpitations and other rhythm-related symptoms, with full-disclosure data analysed and reported by specialists.",
        "The patch can be applied during a short practice visit, with secure data return and structured reporting feeding directly into your clinical pathway.",
      ]}
    />
    <Spotlight
      code="S.04 · 24-hr Ambulatory BP"
      title="24-Hour Ambulatory BP"
      intro="Complete day-and-night blood pressure profiling, worn through daily life."
      testId="abpm-spotlight"
      flip
      image={{ src: "/abpm-heart.png", alt: "Digital heart visual representing continuous blood pressure profiling" }}
      paragraphs={[
        "A compact monitor and cuff record blood pressure and pulse at set intervals across a full 24-hour cycle — capturing daytime activity, night-time dipping and postural changes that a single clinic reading can never show.",
        "Activity, body position and sleep-wake markers are logged alongside every measurement, giving specialists the context behind each reading and a true picture of cardiovascular behaviour.",
        "Recordings are analysed and reported with graphical trends and statistical summaries, feeding directly into your clinical pathway to support hypertension diagnosis and ongoing treatment review.",
      ]}
    />
  </>
);
